// Calendar column ordering for the agenda's location tracks.
//
// Order: My Events → Main Stage → other stages → everything else, alphanumeric
// ("Room 2" before "Room 10", "Room 153" before "Room 210ABC").
//
// `location.order` is Sessionboard's nullable "Display Order". It sequences the
// stages, so the client can reorder them without a deploy — but it is only ever
// a tiebreak WITHIN a tier. It used to be consulted before the tier, so once the
// client set a Display Order on ordinary rooms (Room 160 = 0, Room 153 = 1) those
// rooms sorted ahead of Main Stage. Unknown locations belong at the end no matter
// what their Display Order says, and their stray values must not reshuffle them
// out of natural order either.
//
// Pure + Vue-free so it's safe to import anywhere (a ref/computed in shared/
// crashes the Worker on cold start).

// The synthetic track that holds the user's own custom events on My Schedule.
export const MY_EVENTS_ID = 'my-events';

const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

const TIER_MY_EVENTS = 0;
const TIER_MAIN_STAGE = 1;
const TIER_STAGE = 2;
const TIER_UNKNOWN = 3;

export function locationTier(group) {
    if (group?.location?.id === MY_EVENTS_ID) return TIER_MY_EVENTS;
    const name = (group?.name || '').toLowerCase();
    if (name === 'main stage') return TIER_MAIN_STAGE;
    if (name.includes('stage')) return TIER_STAGE;
    return TIER_UNKNOWN;
}

// null / undefined / '' all mean "no Display Order set". 0 is a real value.
const hasOrder = (value) => value != null && value !== '';

export function compareLocations(a, b) {
    const tierA = locationTier(a);
    const tierB = locationTier(b);
    if (tierA !== tierB) return tierA - tierB;

    // Unknown locations are sorted purely by name — their Display Order is
    // incidental data the client set on rooms, not an intent to rank them.
    if (tierA === TIER_UNKNOWN) return collator.compare(a?.name || '', b?.name || '');

    const orderA = a?.location?.order;
    const orderB = b?.location?.order;
    const aHas = hasOrder(orderA);
    const bHas = hasOrder(orderB);
    if (aHas && bHas && Number(orderA) !== Number(orderB)) return Number(orderA) - Number(orderB);
    if (aHas !== bHas) return aHas ? -1 : 1;

    return collator.compare(a?.name || '', b?.name || '');
}
